/**
 * GroupIcon - iOS-style folder icon showing a 3x3 mini-grid of app icons
 */

"use client";

import { isValidElement, useMemo } from "react";
import type { DesktopIconConfig } from "../types";

export interface GroupIconProps {
  /** The icon configs for apps in this group (first 9 shown) */
  iconConfigs: DesktopIconConfig[];
  /** Display name of the group */
  groupName: string;
  /** Whether the icon is jiggling (edit mode) */
  isJiggling?: boolean;
  /** Group ID for jiggle delay calculation */
  groupId: string;
  /** Whether this is rendered in mobile grid layout */
  isMobileGrid?: boolean;
  /** Whether this is rendered in dock */
  isDock?: boolean;
  /** Click handler */
  onClick?: () => void;
  /** Delete handler (shown in jiggle mode) */
  onDelete?: () => void;
  /** Aggregated notification badge count */
  badgeCount?: number;
  /** Custom class name */
  className?: string;
}

export function GroupIcon({
  iconConfigs,
  groupName,
  isJiggling,
  groupId,
  isMobileGrid,
  isDock,
  badgeCount = 0,
  onClick,
  onDelete,
  className = "",
}: GroupIconProps) {
  const displayIcons = useMemo(() => iconConfigs.slice(0, 9), [iconConfigs]);

  const jiggleDelay = isJiggling
    ? `${(groupId.split("").reduce((a, c) => a + c.charCodeAt(0), 0) % 5) * 0.06}s`
    : undefined;

  const showBadge = badgeCount > 0;
  const badgeLabel = badgeCount > 99 ? "99+" : String(badgeCount);

  // Size variants
  const containerSize = isDock ? "h-[44px] w-[44px]" : "h-[72px] w-[72px]";
  const miniIconSize = isDock ? 12 : 20;
  const gridGap = isDock ? 1 : 2;
  const gridPadding = isDock ? 4 : 6;

  const miniGrid = (
    <div
      className={`${containerSize} overflow-hidden rounded-[18px] bg-white/20 shadow-lg backdrop-blur-md dark:bg-white/10`}
      style={{ padding: gridPadding }}
    >
      <div
        className="grid h-full w-full place-items-center"
        style={{
          gridTemplateColumns: "repeat(3, 1fr)",
          gridTemplateRows: "repeat(3, 1fr)",
          gap: gridGap,
        }}
      >
        {Array.from({ length: 9 }).map((_, i) => {
          const iconConfig = displayIcons[i];
          if (!iconConfig) {
            return <div key={i} />;
          }
          const icon = iconConfig.icon;
          const isString = typeof icon === "string";
          const isElement = isValidElement(icon);
          return (
            <div
              key={iconConfig.id}
              className="flex items-center justify-center overflow-hidden rounded-[4px]"
              style={{ width: miniIconSize, height: miniIconSize }}
            >
              {isString && (
                <span style={{ fontSize: miniIconSize * 0.7, lineHeight: 1 }}>
                  {icon}
                </span>
              )}
              {isElement && (
                <div
                  className="[&>div]:h-full [&>div]:w-full [&>div]:rounded-[3px] [&_svg]:h-full [&_svg]:w-full"
                  style={{ width: miniIconSize, height: miniIconSize }}
                >
                  {icon}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  // Mobile grid mode (matches DesktopIcon's mobile grid layout)
  if (isMobileGrid) {
    return (
      <div
        data-icon-id={groupId}
        onClick={isJiggling ? undefined : onClick}
        className={`flex w-[78px] select-none flex-col items-center ${
          isJiggling ? "" : "cursor-pointer active:scale-90"
        } ${isJiggling ? "animate-jiggle" : "transition-transform duration-100"} ${className}`}
        style={isJiggling ? { animationDelay: jiggleDelay } : undefined}
      >
        <div className="relative">
          {miniGrid}
          {/* Delete badge in jiggle mode */}
          {isJiggling && onDelete && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              className="absolute -left-1.5 -top-1.5 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-gray-500/80 text-[10px] font-bold text-white shadow-md backdrop-blur-sm"
            >
              ✕
            </button>
          )}
          {/* Notification badge */}
          <div
            className={`pointer-events-none absolute -right-[7px] -top-[7px] z-10 flex min-w-[21px] items-center justify-center rounded-full bg-[#FF3B30] px-1 text-[12px] font-bold leading-[19px] text-white transition-all duration-200 ${showBadge ? "scale-100" : "scale-0"}`}
          >
            {badgeLabel}
          </div>
        </div>
        <span className="mt-1 w-full truncate text-center text-[11px] font-medium leading-tight text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
          {groupName}
        </span>
      </div>
    );
  }

  // Dock mode (no label)
  if (isDock) {
    return (
      <div
        data-icon-id={groupId}
        onClick={onClick}
        className={`flex cursor-pointer select-none flex-col items-center transition-transform duration-100 active:scale-90 ${className}`}
      >
        <div className="relative">
          {miniGrid}
          {/* Notification badge */}
          <div
            className={`pointer-events-none absolute -right-[5px] -top-[5px] z-10 flex min-w-[18px] items-center justify-center rounded-full bg-[#FF3B30] px-1 text-[10px] font-bold leading-[16px] text-white transition-all duration-200 ${showBadge ? "scale-100" : "scale-0"}`}
          >
            {badgeLabel}
          </div>
        </div>
      </div>
    );
  }

  // Desktop free mode (larger with label)
  return (
    <div
      data-icon-id={groupId}
      onClick={onClick}
      className={`flex w-[112px] cursor-pointer select-none flex-col items-center transition-transform duration-100 active:scale-95 ${className}`}
    >
      <div className="relative">
        <div
          className="h-[80px] w-[80px] overflow-hidden rounded-[20px] bg-white/20 shadow-lg backdrop-blur-md dark:bg-white/10"
          style={{ padding: 8 }}
        >
          <div
            className="grid h-full w-full place-items-center"
            style={{
              gridTemplateColumns: "repeat(3, 1fr)",
              gridTemplateRows: "repeat(3, 1fr)",
              gap: 3,
            }}
          >
            {Array.from({ length: 9 }).map((_, i) => {
              const iconConfig = displayIcons[i];
              if (!iconConfig) return <div key={i} />;
              const icon = iconConfig.icon;
              const isString = typeof icon === "string";
              const isElement = isValidElement(icon);
              return (
                <div
                  key={iconConfig.id}
                  className="flex items-center justify-center overflow-hidden rounded-[5px]"
                  style={{ width: 20, height: 20 }}
                >
                  {isString && (
                    <span style={{ fontSize: 14, lineHeight: 1 }}>{icon}</span>
                  )}
                  {isElement && (
                    <div
                      className="[&>div]:h-full [&>div]:w-full [&>div]:rounded-[4px] [&_svg]:h-full [&_svg]:w-full"
                      style={{ width: 20, height: 20 }}
                    >
                      {icon}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        {isJiggling && onDelete && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="absolute -left-1.5 -top-1.5 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-gray-500/80 text-[10px] font-bold text-white shadow-md backdrop-blur-sm"
          >
            ✕
          </button>
        )}
        {/* Notification badge */}
        <div
          className={`pointer-events-none absolute -right-[7px] -top-[7px] z-10 flex min-w-[21px] items-center justify-center rounded-full bg-[#FF3B30] px-1 text-[12px] font-bold leading-[19px] text-white transition-all duration-200 ${showBadge ? "scale-100" : "scale-0"}`}
        >
          {badgeLabel}
        </div>
      </div>
      <span className="mt-1.5 w-full truncate text-center text-xs font-medium text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
        {groupName}
      </span>
    </div>
  );
}
